import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../api";
import { ShowToast } from "../../components/toast";
import { PRICING_STREAM_QUERY_KEY } from "./use-pricing-stream";

export interface PricingNotification {
  id: string;
  stayDate: string;
  roomTypePmsId?: string;
  roomTypeName?: string;
  previousPrice?: number | null;
  newPrice?: number;
  priceChange?: number;
  priceChangePercent?: number;
  narrative?: string;
  trigger?: string;
  type?: string;
  isRead?: boolean;
  isSignificant?: boolean;
  createdAt?: string;
  [key: string]: unknown;
}

export interface PricingNotificationsResponse {
  data: PricingNotification[];
  total: number;
  page: number;
  limit: number;
  [key: string]: unknown;
}

const QUERY_KEYS = {
  notifications: "pricingNotifications",
  summary: "pricingNotificationsSummary",
};

export function usePricingNotifications(filters?: {
  dateFrom?: string;
  dateTo?: string;
  type?: string;
  roomType?: string;
  isRead?: boolean;
  isSignificant?: boolean;
  page?: number;
  limit?: number;
}) {
  return useQuery<PricingNotificationsResponse>({
    queryKey: [QUERY_KEYS.notifications, filters],
    queryFn: () => api.pricingNotifications.list(filters) as Promise<PricingNotificationsResponse>,
    staleTime: 30 * 1000,
  });
}

export function usePricingNotificationSummary() {
  return useQuery({
    queryKey: [QUERY_KEYS.summary],
    queryFn: () => api.pricingNotifications.summary(),
    refetchInterval: 60 * 1000,
    staleTime: 30 * 1000,
  });
}

export function useMarkNotificationRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.pricingNotifications.markRead(id),
    onMutate: async (id: string) => {
      // Cancel outgoing refetches so they don't overwrite our optimistic update
      await queryClient.cancelQueries({ queryKey: [QUERY_KEYS.notifications] });
      await queryClient.cancelQueries({ queryKey: [QUERY_KEYS.summary] });

      // Snapshot previous values
      const previousNotifications = queryClient.getQueriesData({ queryKey: [QUERY_KEYS.notifications] });
      const previousSummary = queryClient.getQueryData([QUERY_KEYS.summary]);

      // Optimistically mark isRead in all matching notification caches
      queryClient.setQueriesData<Record<string, unknown>>({ queryKey: [QUERY_KEYS.notifications] }, (old) => {
        if (!old?.data) return old;
        const data = old.data as Array<{ id: string; isRead: boolean }>;
        return {
          ...old,
          data: data.map((n) => n.id === id ? { ...n, isRead: true } : n),
        };
      });

      // Optimistically decrement unread count
      queryClient.setQueryData<Record<string, unknown>>([QUERY_KEYS.summary], (old) => {
        if (!old) return old;
        return { ...old, unread: Math.max(0, ((old.unread as number) ?? 1) - 1) };
      });

      return { previousNotifications, previousSummary };
    },
    onError: (_err, _id, context) => {
      // Rollback on error
      if (context?.previousNotifications) {
        for (const [key, data] of context.previousNotifications) {
          queryClient.setQueryData(key, data);
        }
      }
      if (context?.previousSummary) {
        queryClient.setQueryData([QUERY_KEYS.summary], context.previousSummary);
      }
    },
    onSettled: () => {
      // Refetch summary in background (lightweight) but NOT the full notification list
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.summary] });
    },
  });
}

export function useMarkAllNotificationsRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.pricingNotifications.markAllRead(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.notifications] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.summary] });
    },
  });
}

export function useApproveRecommendation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, adjustedValue }: { id: string; adjustedValue?: number }) =>
      api.pricingNotifications.approve(id, adjustedValue != null ? { adjustedValue } : undefined),
    onSuccess: (data) => {
      ShowToast({
        message: (data as { message?: string } | undefined)?.message || "Floor updated. Prices recalculating...",
        type: "success",
      });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.notifications] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.summary] });
      queryClient.invalidateQueries({ queryKey: [PRICING_STREAM_QUERY_KEY] });
    },
    onError: () => {
      ShowToast({ message: "Failed to approve recommendation", type: "error" });
    },
  });
}

export function usePricingNotificationVolume(dateRange?: {
  dateFrom?: string;
  dateTo?: string;
}) {
  return useQuery({
    queryKey: [QUERY_KEYS.summary, "volume", dateRange],
    queryFn: () => api.pricingNotifications.volume(dateRange),
    staleTime: 5 * 60 * 1000,
  });
}

export function useDismissRecommendation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.pricingNotifications.dismiss(id),
    onSuccess: () => {
      ShowToast({ message: "Recommendation dismissed", type: "success" });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.notifications] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.summary] });
      queryClient.invalidateQueries({ queryKey: [PRICING_STREAM_QUERY_KEY] });
    },
    onError: () => {
      ShowToast({ message: "Failed to dismiss recommendation", type: "error" });
    },
  });
}
